import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';

import { FavouritesConfig, GridConfig } from '@frontend/sports/common/client-config-data-access';
import { FavouritesService } from '@frontend/sports/favourites/core/feature';
import { FavouritesDashboardUrlParam } from '@frontend/sports/navigation/core/feature/url-helper';
import { FavouritesGridPayload } from '@frontend/sports/types/components/widget/types';
import { PrimaryNavigationDisplayType } from '@frontend/sports/types/configuration';
import { WidgetLoaderService, WidgetSkeletonData, WidgetSkeletonModel } from '@frontend/sports/widget/core/feature';
import { WidgetHeaderComponent } from '@frontend/sports/widget/feature/header';
import { WidgetPillComponent } from '@frontend/sports/widget/feature/pills';
import { WidgetTabBarComponent, WidgetTabBarDisplay } from '@frontend/sports/widget/feature/tab-bar';
import { DsCard } from '@frontend/ui/card';
import { orderBy } from 'lodash-es';
import { BehaviorSubject, map } from 'rxjs';

import { FavouritesFilterService } from '../favourites-dashboard/favourites-filters.service';
import { FavouritesGridWidgetService } from './favourites-grid-widget.service';
import { FavouritesGridNavigation } from './favourites-grid.model';

@Component({
    selector: 'ms-favourites-widget-skeleton',
    templateUrl: './favourites-widget-skeleton.component.html',
    styleUrl: './favourites-widget-skeleton.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'tabbed-grid-widget grid-wrapper',
    },
    imports: [AsyncPipe, WidgetPillComponent, WidgetTabBarComponent, WidgetHeaderComponent, DsCard],
})
export class FavouritesWidgetSkeletonComponent implements WidgetSkeletonModel {
    private readonly favouritesGridWidget = inject(WidgetSkeletonData<FavouritesGridPayload>);
    private readonly favouritesService = inject(FavouritesService);
    private readonly favouritesFilterService = inject(FavouritesFilterService);
    private readonly favouritesGridWidgetService = inject(FavouritesGridWidgetService);
    private readonly gridConfig = inject(GridConfig);
    private readonly widgetLoaderService = inject(WidgetLoaderService);
    private readonly favouritesConfig = inject(FavouritesConfig);

    private readonly ALL_SPORTS_MENU_ITEM_ID = '-1';

    protected showPrimaryNavAsPill = this.gridConfig.primaryNavigationType === PrimaryNavigationDisplayType.Pill;
    protected title = this.favouritesGridWidget.widget.payload?.title ?? '';
    protected skeletonEvents = signal([]);
    protected favouritesGridNavigation$ = this.favouritesService.getList(true).pipe(
        map((favourites) => {
            // Unblock the widgets under this as soon as we have the data to render the skeleton in its correct size
            this.widgetLoaderService.setWidgetLoaded(`${this.favouritesGridWidget.widget.location}-${this.favouritesGridWidget.widget.id}`);
            const favouritesViewModel = this.favouritesFilterService
                .getGeneralPills(FavouritesDashboardUrlParam.Upcoming, favourites, true)
                .filter((sport) => sport.id !== this.ALL_SPORTS_MENU_ITEM_ID && !!sport.count);

            const orderedFavourites = orderBy(favouritesViewModel, (favourite) => favourite.count, 'desc');

            if (orderedFavourites.length > 0) {
                const maxEventCount = Math.min(orderedFavourites[0].count ?? 0, this.favouritesConfig.favouritesGridWidgetFixturesMaxCount);
                this.skeletonEvents.set(Array.from({ length: maxEventCount }));

                const orderedNavigation = orderedFavourites.map<FavouritesGridNavigation>((favourite, index) => ({
                    ...favourite,
                    display: WidgetTabBarDisplay.Default,
                    context: {},
                    itemId: favourite.id ?? '',
                    widgetId: this.favouritesGridWidget.widget.id,
                    active: false,
                    count: 0,
                    index: index.toString(),
                }));

                this.favouritesGridWidgetService.setWidgetNavigation(orderedNavigation);
                this.favouritesGridWidgetService.loadActiveSport(orderedNavigation[0].id);
                return orderedNavigation;
            } else {
                return [];
            }
        }),
    );

    showSkeleton$ = new BehaviorSubject(true);
}
